import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { taskStatusMocked } from '../../constants';
import styles from './FilterChips.module.scss';
import { PatientFilterChip } from 'components';
import { RootState } from 'store';
import { getUsers } from 'store/users/selectors';
import { useDispatch, useSelector } from 'store/utils';
import { GetPatientCasesParams, TreatmentPlanTask } from 'types';

type Props = {
  filterSelector: (state: RootState) => GetPatientCasesParams;
  actionCreator: ActionCreatorWithPayload<GetPatientCasesParams, string>;
};

export const FilterChips = ({ filterSelector, actionCreator }: Props) => {
  const dispatch = useDispatch();
  const filters = useSelector(filterSelector);
  const users = useSelector(getUsers);

  const { page, ...filtersWithoutPage } = filters;

  if (
    !Object.values(filtersWithoutPage).filter((value) =>
      Array.isArray(value) ? Boolean(value.length) : Boolean(value),
    ).length
  )
    return null;

  const doctors = filters.doctorIds?.map((doctor) => ({ id: doctor, name: users.find((u) => u.id === doctor)?.name }));
  const clinics = filters.clinicIds?.map((clinic) => ({ id: clinic, name: users.find((u) => u.id === clinic)?.name }));
  const tasks = filters.tasks
    ?.map((task) => ({ id: task, name: taskStatusMocked.find((t) => t.id === task)?.name }))
    .filter((task) => Boolean(task.name));

  const treatmentPlanTypes = filters.treatmentPlanTypes?.map((plan) => ({ id: plan, name: plan }));

  const handleFilterChange = (id: string, param: 'doctorIds' | 'clinicIds' | 'tasks' | 'treatmentPlanTypes') => {
    const requestParams = {
      ...filters,
      [param]: filters?.[param]?.filter((el) => el !== id),
    };

    if (param === 'treatmentPlanTypes' && !requestParams.treatmentPlanTypes?.length) {
      requestParams.tasks = requestParams?.tasks?.filter((task) => task !== TreatmentPlanTask.IN_TREATMENT);
    }

    dispatch(actionCreator(requestParams));
  };

  const handleArchiveFilterChange = () => {
    const requestParams = {
      ...filters,
      isArchived: false,
    };

    dispatch(actionCreator(requestParams));
  };

  const handleBlockedFilterChange = () => {
    const requestParams = {
      ...filters,
      // isTaskBlocked: false,
    };
    delete requestParams.isTaskBlocked;

    dispatch(actionCreator(requestParams));
  };

  return (
    <div className={styles.filtersBlockWrapper}>
      {clinics &&
        clinics.map((el) => (
          <PatientFilterChip label={el.name} onClick={() => handleFilterChange(el.id, 'clinicIds')} key={el.id} />
        ))}
      {doctors &&
        doctors.map((el) => (
          <PatientFilterChip label={el.name} onClick={() => handleFilterChange(el.id, 'doctorIds')} key={el.id} />
        ))}
      {tasks &&
        tasks.map((el) => (
          <PatientFilterChip label={el.name} onClick={() => handleFilterChange(el.id, 'tasks')} key={el.id} />
        ))}
      {treatmentPlanTypes &&
        treatmentPlanTypes.map((el) => (
          <PatientFilterChip
            label={el.name}
            onClick={() => handleFilterChange(el.id, 'treatmentPlanTypes')}
            key={el.id}
          />
        ))}
      {filters.isArchived && <PatientFilterChip label="Archived" onClick={handleArchiveFilterChange} />}
      {filters.isTaskBlocked && <PatientFilterChip label="Out of treatment" onClick={handleBlockedFilterChange} />}
    </div>
  );
};
